import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppError } from '../models/app-error';
import { assertNonNull } from '../utils/assert-non-null';
import { ContentType } from '../enums/content-type';

import { AppConfigService } from './app-config.service';
import { type FileUploadService } from './file-upload';
import { FileService } from './file-service';
import { type S3PresignedUrlDto } from './mappers/dto/s3-presigned-url.dto';
import { S3PresignedUrlRequestMapper } from './mappers/s3-presigned-url-request.mapper';

/** Service for uploading files to S3 storage. */
@Injectable()
export class S3UploadService implements FileUploadService {
	private readonly httpClient = inject(HttpClient);

	private readonly fileService = inject(FileService);

	private readonly s3PresignedUrlRequestMapper = inject(S3PresignedUrlRequestMapper);

	private readonly presignUrl: URL;

	public constructor() {
		const appConfigService = inject(AppConfigService);

		this.presignUrl = new URL('media/s3-presigned-url/', appConfigService.apiUrl);
	}

	/** @inheritdoc */
	public upload(file: File, destination: string): Observable<string> {
		const body = this.s3PresignedUrlRequestMapper.toDto({
			destination,
			contentType: this.getContentTypeFrom(file),
			extension: this.fileService.extractExtension(file),
		});

		return this.httpClient.post<S3PresignedUrlDto>(this.presignUrl.toString(), body).pipe(
			switchMap(presignedUrl => {
				// Set response type 'text' because s3 returns XML.
				// Using Object type to avoid errors connected with responseType
				const options: object = { observe: 'body', responseType: 'text' };

				return this.httpClient.post<string>(
					presignedUrl.url,
					this.createFormDataFromPresignedUrl(presignedUrl, file),
					options,
				);
			}),
			map(xml => this.parseLocationFromXml(xml)),

			// S3 returns encoded URL, decode it as the client expects it
			map(decodeURIComponent),
		);
	}

	private createFormDataFromPresignedUrl(presignedUrl: S3PresignedUrlDto, file: File): FormData {
		const formData = new FormData();

		Object.entries(presignedUrl.fields).forEach(([field, value]) => {
			formData.append(field, value.toString());
		});

		// !!! Important to add a file as the last field of form data. https://stackoverflow.com/a/15235866
		formData.append('file', file, file.name);

		return formData;
	}

	private parseLocationFromXml(xmlString: string): string {
		const parser = new DOMParser();
		const xmlDocument = parser.parseFromString(xmlString, 'application/xml').documentElement;
		const location = xmlDocument.querySelector('Location')?.textContent;
		assertNonNull(location);

		return location;
	}

	private getContentTypeFrom(file: File): ContentType {
		const contentType = this.fileService.fromFile(file);
		if (contentType === ContentType.Unknown) {
			throw new AppError(`${file.type} file type is not supported yet`);
		}
		return contentType;
	}
}
